import {
  Component,
  Directive,
  ElementRef,
  OnInit,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  model,
  untracked
} from '@angular/core';
import { animate } from 'motion';
import { AnimatableDirective } from '../animation/animatable.directive';


@Directive({
  selector: '[expandable]',
})
export class ExpandableDirective {
  /**
 * Two-way bindable signal for the expanded state.
 * When true, the element expands to its full height.
 * When false, the element collapses to height 0.
 */
  public readonly expanded = model<boolean>(false);
}

/**
 * Component that animates an element's height expansion and collapse.
 * Uses an inner wrapper pattern to avoid padding collapse issues.
 *
 * @example
 * ```html
 * <div expandable [(expanded)]="isExpanded" [duration]="0.5">
 *   <div class="p-6">
 *     Content that will expand and collapse
 *   </div>
 * </div>
 * ```
 */
@Component({
  standalone: true,
  selector: 'div[expandable]',
  template: '<ng-content></ng-content>',
  host: {
    '[style.overflow]': 'overflow()',
    '[style.display]': `'block'`,
  },
  hostDirectives: [
    {
      directive: ExpandableDirective,
      inputs: ['expanded'],
      outputs: ['expandedChange'],
    },
    AnimatableDirective
  ]
})
export class ExpandableComponent implements OnInit {

  private readonly directive = inject(ExpandableDirective, { self: true });
  private readonly animatable = inject(AnimatableDirective, { self: true });
  private readonly el = inject(ElementRef<HTMLElement>);

  /**
   * Animation duration in seconds.
   * @default 0.15
   */
  public readonly duration = input<number>(0.15);

  protected readonly overflow = computed(() => this.animatable.animating() || !this.directive.expanded() ? 'hidden' : null);


  private currentHeight = 0;
  private currentAnimation: ReturnType<typeof animate> | null = null;

  private readonly initialized = new Promise<void>((resolve, reject) => {
    afterNextRender(() => {
      try {
        const element = this.el.nativeElement;
        const initialHeight = this.directive.expanded() ? element.scrollHeight : 0;

        this.currentHeight = initialHeight;
        element.style.height = `${initialHeight}px`;
        resolve();
      }
      catch (err) {
        reject(err);
      }
    });
  });

  constructor() {
    // React to changes in the expanded state
    effect(async () => {
      const expanded = this.directive.expanded();

      await this.initialized;
      this.animate(expanded);
    });
  }

  private async animate(shouldExpand: boolean) {
    this.animatable.animationStart.emit();
    try {
      const element = this.el.nativeElement;
      const duration = untracked(this.duration);

      // Cancel any running animation
      if (this.currentAnimation) {
        this.currentAnimation.cancel();
        this.currentAnimation = null;
        this.currentHeight = element.getBoundingClientRect().height;
      }

      const targetHeight = shouldExpand ? element.scrollHeight : 0;

      // Animate the element itself
      this.currentAnimation = animate(
        element,
        { height: [`${this.currentHeight}px`, `${targetHeight}px`] },
        { duration }
      );

      await this.currentAnimation.finished;

      // Remove height style when expanded to allow dynamic content changes
      if (shouldExpand) {
        element.style.height = '';
      }

      this.currentHeight = targetHeight;
      this.currentAnimation = null;
    }
    finally {
      this.animatable.animationEnd.emit();
    }

  }

  ngOnInit(): void {
    // Ensure initial height is set for SSR
    if (typeof window === 'undefined') {
      this.el.nativeElement.style.height = this.directive.expanded() ? 'auto' : '0';
    }
  }
}
